import { Button, Dialog, Field, Icon, Select } from '@dayon/design-system';
import { ACTION_TITLE, NOTE_MINIMUM, REASONS } from './vocabulary.ts';
import { scopeLabel } from './format.ts';
import type { ActionKind, AdminRow, MatrixPayload } from './types.ts';

/**
 * One dialog for the six member writes.
 *
 * They share a target, a reason code and a note, and six near-identical dialogs
 * would drift apart — the reason floor enforced in five of them and not the
 * sixth is exactly the kind of drift that ends up in the audit trail. What
 * differs per action is the sentence at the top (what will actually happen) and
 * the one extra field two of them need.
 */
export function ActionDialog({
  action,
  admin,
  matrix,
  brands,
  saving,
  errors,
  reasonCode,
  onReasonCode,
  note,
  onNote,
  nextRole,
  onNextRole,
  nextBrands,
  onNextBrands,
  roleNeedsFactor,
  twoFactorRoles,
  onSubmit,
  onClose,
}: {
  action: ActionKind | null;
  admin: AdminRow | null;
  matrix: MatrixPayload | null;
  brands: { id: string; name: string }[];
  saving: boolean;
  errors: Record<string, string>;
  reasonCode: string;
  onReasonCode: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
  nextRole: string;
  onNextRole: (v: string) => void;
  nextBrands: string[];
  onNextBrands: (v: string[]) => void;
  roleNeedsFactor: boolean;
  twoFactorRoles: Set<string>;
  onSubmit: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={action !== null && admin !== null}
      onClose={onClose}
      title={action && admin ? `${ACTION_TITLE[action]} — ${admin.display_name}` : ''}
      wide
      // A write in flight must finish or fail; Escape mid-request would leave
      // the operator unsure which.
      dismissible={!saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving…' : action ? ACTION_TITLE[action] : ''}
          </Button>
        </>
      }
    >
      {action && admin && (
        <div className="stack">
          {/* THE SENTENCES BELOW PROMISE THE EFFECT; THE IDENTIFIERS LIVE HERE.
              An operator about to change somebody's role, or bar their account,
              cannot do anything with the name of a table or a column — it is not
              something they can look up, ask for, or repeat to the person they
              are about to affect. What they need is when the change bites and
              what it does not touch, which is what each notice now says.

              The mechanism, for whoever is reading the source rather than the
              screen. A role change is honoured because a principal's
              capabilities are selected from identity.role_permission on every
              request and never written into the session cookie, which is why the
              holder stays signed in and still binds at their next action. A
              suspension increments identity.admin_user.token_version — the
              number carried as the session's `tv` claim and compared on every
              request — and revokes identity.refresh_token and
              identity.device_trust for them, so the sessions stop at once rather
              than lingering until they would have expired. A brand-scope change
              sets identity.admin_brand_scope.revoked_at rather than deleting the
              grant, and every reader filters `revoked_at IS NULL`, which is how
              the history of who could reach what survives a narrowing. Keep this
              comment in step with the prose: if one of these mechanisms changes,
              a notice below is making a promise the platform no longer keeps. */}
          <div className="notice notice-quiet">
            <Icon
              name={action === 'suspend' || action === 'reset2fa' ? 'warning-circle' : 'info'}
            />
            <div>
              {action === 'role' ? (
                <>
                  The new role binds at {admin.display_name}&rsquo;s <strong>next request</strong>,
                  not their next sign-in: what a role is allowed to do is re-checked on every action
                  they take, and is never written into the sign-in cookie their browser is already
                  holding. They stay signed in. The change and its entry in the audit trail are
                  written together.
                </>
              ) : action === 'suspend' ? (
                <>
                  Suspending bars the account and ends every session it holds at once: the stamp on
                  the account that every request is checked against moves, and the tokens that keep
                  them signed in between visits and the devices they had marked as trusted are
                  cancelled with it. It does not undo anything they did: items where they are the
                  maker stay pending a different checker, and what they have already done stays in
                  the audit trail, which can only ever be added to.
                </>
              ) : action === 'reinstate' ? (
                <>
                  Reinstating lets {admin.display_name} sign in again. It does NOT restore the
                  sessions the suspension ended — those stay dead, so they sign in afresh.
                </>
              ) : action === 'scope' ? (
                <>
                  The scope below <strong>replaces</strong> what {admin.display_name} has now, which
                  is {scopeLabel(admin.brands).toLowerCase()}. Taking a brand away records that
                  access as withdrawn, and when, rather than erasing it, so the history of who could
                  reach what survives — and it binds at their next request, because the brands an
                  admin may see are re-checked on every action they take, alongside what their role
                  may do. You cannot grant a brand outside your own scope.
                </>
              ) : action === 'reset2fa' ? (
                <>
                  <strong>Nothing is sent.</strong> This clears the enrolment: the stored secret is
                  overwritten with one nobody has seen, the recovery codes are voided, and every
                  session, refresh token and trusted device ends. {admin.display_name} enrols a new
                  authenticator themselves at their next sign-in by scanning a code on their own
                  screen — which is the only safe way an authenticator secret ever moves.
                  {twoFactorRoles.has(admin.role)
                    ? ' Their role mandates a factor, so sign-in will hand them an enrolment rather than a session until they finish.'
                    : ''}
                </>
              ) : (
                <>
                  Every session {admin.display_name} holds ends at once — the cookie, the refresh
                  tokens and the trusted devices — and the account stays{' '}
                  <strong>{(admin.status ?? '').toLowerCase()}</strong>. They can sign straight back
                  in, which is the whole difference between this and a suspension.
                </>
              )}
            </div>
          </div>

          {action === 'role' && (
            <Field
              label="New role"
              hint="One role per admin. A role carrying a money capability needs an enrolled authenticator app."
              error={errors['roleName']}
            >
              {({ id, describedBy, invalid }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={nextRole}
                  onChange={(e) => onNextRole(e.target.value)}
                >
                  {(matrix?.roles ?? []).map((r) => (
                    <option key={r.name} value={r.name}>
                      {r.name}
                      {twoFactorRoles.has(r.name) ? ' · 2FA required' : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {action === 'role' && roleNeedsFactor && (
            <div className="notice notice-quiet">
              <Icon name="lock-simple" />
              <div>
                {nextRole} carries a capability the platform honours only from a session verified
                with two-factor authentication, and {admin.display_name} shows{' '}
                <strong>2FA {admin.two_factor}</strong>. Given this role they would appear
                authorised on the roster and be refused on every privileged click, so the platform
                refuses the change. Enrol the factor first, or choose a role without one.
              </div>
            </div>
          )}

          {action === 'scope' && (
            <Field
              label="Brands this admin may see"
              hint="Select none for every brand — an empty scope is the absence of a restriction, which is the widest scope there is."
              error={errors['brandIds']}
            >
              {({ id, describedBy, invalid }) => (
                <select
                  id={id}
                  className="input"
                  multiple
                  size={Math.min(Math.max(brands.length, 3), 6)}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={nextBrands}
                  onChange={(e) =>
                    onNextBrands(
                      [...e.target.selectedOptions].map((o) => o.value),
                    )
                  }
                >
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field
            label="Reason"
            hint="A closed set: an access review counts the codes, and prose cannot be counted."
            error={errors['reasonCode']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={reasonCode}
                onChange={(e) => onReasonCode(e.target.value)}
              >
                {REASONS[action].map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint={`At least ${NOTE_MINIMUM} characters. The code says what kind of change this was; the note is what makes this one readable a year from now.`}
            error={errors['note']}
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={note}
                onChange={(e) => onNote(e.target.value)}
                placeholder="Who asked for this, and against what ticket or approval"
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
